import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { DocumentQuoteItem } from '../../../model/document.model';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserService } from '../../../services/user.service';
import { AddInvoice } from '../../../model/add-invoice.model';
import { ToolbarService } from '../../../services/toolbar.service';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { EscapeDialogHelper } from '../../../helpers/escape-dialog.helper';
import { UploadFilesComponent } from '../upload-files/upload-files.component';
import { mediumModalConfig } from '../../../consts/modal-config.const';
import { ModalConfigsHelper } from '../../../helpers/modal-configs.helper';
import { CurrentUserService } from '../../../services/currentUser.service';
import { ParseXMLService } from '../../../services/parseXML.service';
import { DecimalPipe, I18nPluralPipe, NgClass } from '@angular/common';
import { fadeSlideUpDown } from '../../../animations/fade-slide-up-down.animation';
import { fadeInOut } from '../../../animations/fade-in-out.animation';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatCheckbox } from '@angular/material/checkbox';
import { ImportFileFromDiskComponent } from '../../../components/import-file-from-disk/import-file-from-disk.component';
import { HighlightStatusDirective } from '../../../directives/highlight-status.directive';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { BrowserAutoFillDisabledDirective } from '../../../directives/browser-auto-fill-disabled.directive';
import { FormatInputNumberDirective } from '../../../directives/format-input-number.directive';
import { DatePipe } from '../../../pipes/date.pipe';
import { TimePipe } from '../../../pipes/time.pipe';

@Component({
  selector: 'app-add-invoice',
  templateUrl: './add-invoice.component.html',
  providers: [DecimalPipe],
  animations: [fadeSlideUpDown, fadeInOut],
  styles: [
    `
      .quote-list-grid {
        display: grid;
        grid-template-columns: auto 90px;
        height: 65px;
        padding: 0 10px;
        align-items: center;
      }
      .total {
        display: inline-flex;
        align-items: center;
        justify-content: flex-end;
        padding: 10px;
        width: 45%;
      }
    `
  ],
  imports: [
    MatDialogTitle,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    MatDivider,
    MatDialogContent,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    NgClass,
    MatProgressBar,
    MatCheckbox,
    ImportFileFromDiskComponent,
    MatButton,
    HighlightStatusDirective,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    BrowserAutoFillDisabledDirective,
    MatError,
    FormatInputNumberDirective,
    MatDialogActions,
    DecimalPipe,
    I18nPluralPipe,
    DatePipe,
    TimePipe
  ]
})
export class AddInvoiceComponent implements OnInit {
  #fb = inject(FormBuilder);
  #destroyRef = inject(DestroyRef);
  #dialog = inject(MatDialog);
  #dialogRef = inject(MatDialogRef<AddInvoiceComponent>);
  #escapeDialogHelper = inject(EscapeDialogHelper);
  #messagingService = inject(MessagingService);
  #modalConfigsHelper = inject(ModalConfigsHelper);
  #parseXMLService = inject(ParseXMLService);
  #toolbarService = inject(ToolbarService);
  #userService = inject(UserService);
  decimalPipe = inject(DecimalPipe);
  data = inject(MAT_DIALOG_DATA);

  imagesPluralMapping = { '=1': '1 image', other: '# images' };
  invoiceForm: FormGroup;
  quotes: DocumentQuoteItem[];
  assessedTotalInvoice = 0;
  assessedSubTotalInvoice = 0;
  assessedGSTTotalInvoice = 0;
  selectedIndex = 0;
  importInvoiceFiles: any;
  uploadImages = true;

  fileDeleted = signal<boolean>(false);
  fileName = signal<string>(undefined);
  imageFiles = signal<any>(undefined);
  invoiceFiles = signal<any>(undefined);
  invoiceTotal = signal<number>(0);
  lastModified = signal<number>(undefined);
  pending = signal(false);
  pendingImages = signal(false);
  timezone = inject(CurrentUserService).currentTimeZone;

  ngOnInit(): void {
    this.quotes = this.data.authorizedQuotes as DocumentQuoteItem[];
    this.quotes.sort((a, b) => a.document.assessedDateTime - b.document.assessedDateTime);
    this.assessedTotalInvoice = this.quotes.reduce((acc, item) => acc + item.document.assessedTotal, 0);
    this.assessedSubTotalInvoice = this.quotes.reduce((acc, item) => acc + item.document.assessedSubTotal, 0);
    this.assessedGSTTotalInvoice = this.quotes.reduce((acc, item) => acc + item.document.assessedGSTTotal, 0);
    this.invoiceForm = this.#fb.group({
      invoiceNumber: ['', Validators.required],
      ownerContribution: this.data.ownerContribution || ''
    });
    this.ownerContribution.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.calculateInvoiceTotal());
    this.#escapeDialogHelper.escapeDialog(this.#dialogRef);
    this.selectedIndex = this.data?.selectedIndex;
    this.calculateInvoiceTotal();
  }

  async importInvoice(invoiceInput: any) {
    if (invoiceInput.files.length) {
      this.pending.set(true);
      const xmlValid = await this.#parseXMLService.checkXmlValidity(invoiceInput.files[0]);
      if (xmlValid) {
        this.pendingImages.set(true);
        this.fileDeleted.set(false);
        this.fileName.set(invoiceInput.files[0].name);
        this.lastModified.set(invoiceInput.files[0].lastModified);
        this.invoiceFiles.set(invoiceInput.files);
        this.importInvoiceFiles = invoiceInput.files;
      } else {
        invoiceInput.value = '';
      }
      this.pending.set(false);
    }
  }

  calculateInvoiceTotal() {
    const doCalculations =
      !this.data.lastInvoice || (this.data.lastInvoice?.isFirstInvoice && this.data.lastInvoice?.status === 'REJECTED');
    const total = doCalculations
      ? this.assessedTotalInvoice - (this.data.excess ?? 0) - (this.ownerContribution.value ?? 0)
      : this.assessedTotalInvoice;

    this.invoiceTotal.set(total);
  }

  setFileFromHandler(file: any) {
    this.pendingImages.set(true);
    this.fileName.set(file?.name);
    this.lastModified.set(file?.lastModified);
    this.invoiceFiles.set(file);
  }

  setImages(images: any[]) {
    this.imageFiles.set(images);
    this.pendingImages.set(false);
  }

  deleteFile(invoiceInput: any) {
    this.fileName.set('');
    this.lastModified.set(undefined);
    this.invoiceFiles.set(undefined);
    delete this.importInvoiceFiles;
    invoiceInput.value = '';
    this.fileDeleted.set(true);
    this.imageFiles.set(null);
  }

  addInvoice(importFromQuote: boolean) {
    if (importFromQuote) {
      this.pending.set(true);
      this.#toolbarService.setCloudState('SAVING');
      const invoiceToAdd: AddInvoice = {};
      invoiceToAdd.jobId = this.data.jobId;
      invoiceToAdd.invoiceNumber = this.invoiceNumber.value;
      invoiceToAdd.invoiceDate = '';
      this.invoiceForm.disable();
      this.#userService.addInvoice(invoiceToAdd, this.data.jobId).subscribe({
        next: () => {
          if (
            !this.data.lastInvoice ||
            (this.data.lastInvoice.isFirstInvoice && this.data.lastInvoice.status === 'REJECTED')
          ) {
            this.#userService
              .updateJob(
                (this.ownerContribution.value ?? '').toString().replace(/,/g, ''),
                this.data.jobId,
                'ownerContribution'
              )
              .subscribe({
                next: () => {
                  this.#toolbarService.setCloudState('RESTING');
                  this.pending.set(false);
                  this.#dialogRef.close('Success');
                },
                error: () => {
                  this.invoiceForm.enable();
                  this.#messagingService.error('Could not update Owner Contribution.');
                  this.#toolbarService.setCloudState('RESTING');
                }
              });
          } else {
            this.#toolbarService.setCloudState('RESTING');
            this.pending.set(false);
            this.#dialogRef.close('Success');
          }
        },
        error: (error) => {
          this.invoiceForm.enable();
          if (error.error === 'DUPLICATE_INVOICE_NUMBER') {
            this.invoiceNumber.setErrors({ duplicateInvoice: true });
          } else {
            this.#messagingService.error('Could not add invoice.');
          }
          this.#toolbarService.setCloudState('RESTING');
          this.pending.set(false);
        }
      });
    } else {
      const data = {
        jobId: this.data.jobId,
        files: this.invoiceFiles(),
        invoiceNumber: this.invoiceNumber.value,
        isInvoice: true
      };
      this.#dialog
        .open(UploadFilesComponent, this.#modalConfigsHelper.buildMediumModalConfig(data, mediumModalConfig))
        .afterClosed()
        .subscribe((result) => {
          if (result === 'Success') {
            this.#messagingService.confirm('Invoice Uploaded');
            this.uploadImages && this.imageFiles()?.length ? this.addImages() : this.#dialogRef.close('Success');
          }
        });
    }
  }

  addImages() {
    const data = { jobId: this.data.jobId, files: this.imageFiles(), isPhotos: true };
    this.#dialog
      .open(UploadFilesComponent, this.#modalConfigsHelper.buildMediumModalConfig(data, mediumModalConfig))
      .afterClosed()
      .subscribe((result) => {
        if (result === 'Success') {
          this.#messagingService.confirm(`Photos Uploaded`);
        }
        this.#dialogRef.close('Success');
      });
  }

  formatOwnerContribution() {
    this.ownerContribution.setValue(
      this.ownerContribution.value ? this.decimalPipe.transform(this.ownerContribution.value, '1.2-2') : '',
      { emitEvent: false }
    );
    this.ownerContribution.setErrors(null);
  }

  get invoiceNumber() {
    return this.invoiceForm?.controls.invoiceNumber;
  }
  get ownerContribution() {
    return this.invoiceForm?.controls.ownerContribution;
  }
}
